import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Loader2 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useQuestionnaires } from '@/hooks/use-questionnaires';

const createQuestionnaireSchema = z.object({
  nameEn: z.string().min(1, 'English name is required'),
  nameAr: z.string().min(1, 'Arabic name is required'),
  descriptionEn: z.string().optional(),
  descriptionAr: z.string().optional(),
  isDefault: z.boolean().default(false)
});

type CreateQuestionnaireForm = z.infer<typeof createQuestionnaireSchema>;

interface CreateQuestionnaireDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateQuestionnaireDialog({ open, onOpenChange }: CreateQuestionnaireDialogProps) {
  const { t, dir } = useLanguage();
  const { currentUser } = useAuth();
  const { createQuestionnaire } = useQuestionnaires();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<CreateQuestionnaireForm>({
    resolver: zodResolver(createQuestionnaireSchema),
    defaultValues: {
      nameEn: '',
      nameAr: '',
      descriptionEn: '',
      descriptionAr: '',
      isDefault: false
    }
  });

  const onSubmit = async (data: CreateQuestionnaireForm) => {
    if (!currentUser) return;

    try {
      setIsSubmitting(true);
      
      const name = {
        en: data.nameEn,
        ar: data.nameAr
      };
      
      const description = (data.descriptionEn || data.descriptionAr) ? {
        en: data.descriptionEn || '',
        ar: data.descriptionAr || ''
      } : undefined;

      await createQuestionnaire(name, description, currentUser.uid, data.isDefault);
      
      form.reset();
      onOpenChange(false);
    } catch (error) {
      console.error('Error creating questionnaire:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]" dir={dir}>
        <DialogHeader>
          <DialogTitle>{t('dashboard.admin.questionnaires.createNew')}</DialogTitle>
          <DialogDescription>
            {t('dashboard.admin.questionnaires.createDescription')}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="nameEn"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('dashboard.admin.questionnaires.nameEn')}</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter English name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="nameAr"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('dashboard.admin.questionnaires.nameAr')}</FormLabel>
                    <FormControl>
                      <Input placeholder="أدخل الاسم بالعربية" {...field} dir="rtl" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="descriptionEn"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('dashboard.admin.questionnaires.descriptionEn')}</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Enter English description (optional)" 
                        className="min-h-[80px]"
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="descriptionAr"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('dashboard.admin.questionnaires.descriptionAr')}</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="أدخل الوصف بالعربية (اختياري)" 
                        className="min-h-[80px]"
                        dir="rtl"
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="isDefault"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>
                      {t('dashboard.admin.questionnaires.setAsDefault')}
                    </FormLabel>
                    <p className="text-sm text-muted-foreground">
                      {t('dashboard.admin.questionnaires.defaultDescription')}
                    </p>
                  </div>
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('dashboard.admin.questionnaires.create')}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
